import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus, Search, MapPin, Star, Zap, BarChart3,
  Eye, Download, ArrowRight, Trash2,
} from "lucide-react";
import { useState } from "react";
import { useLocationStore } from "@/lib/store";
import { useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { Location } from "@shared/schema";

interface EnrichedLocation extends Location {
  tokenBalance: number;
  ownerEmail: string | null;
}

const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  active: { bg: "bg-emerald-500/10", text: "text-emerald-500", label: "Active" },
  pending: { bg: "bg-amber-500/10", text: "text-amber-500", label: "Pending Setup" },
  inactive: { bg: "bg-muted", text: "text-muted-foreground", label: "Inactive" },
};

function LocationCard({
  loc,
  isSelected,
  isAdmin,
  onViewDashboard,
  onDelete,
}: {
  loc: EnrichedLocation;
  isSelected: boolean;
  isAdmin: boolean;
  onViewDashboard: () => void;
  onDelete: () => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const statusConfig = STATUS_COLORS[loc.status || "active"] || STATUS_COLORS.active;

  return (
    <Card
      className={`relative overflow-hidden transition-all ${
        isSelected ? "border-l-4 border-l-brand-orange" : ""
      }`}
      data-testid={`card-location-${loc.id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h3 className="font-semibold text-sm truncate" data-testid={`text-name-${loc.id}`}>
              {loc.name}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
              <MapPin className="w-3 h-3 shrink-0" />
              <span className="truncate">{loc.city} · {loc.category}</span>
            </p>
            {isAdmin && loc.ownerEmail && (
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                {loc.ownerEmail}
              </p>
            )}
          </div>
          <Badge variant="outline" className={`${statusConfig.bg} ${statusConfig.text} shrink-0 text-[10px]`}>
            {statusConfig.label}
          </Badge>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="p-2 rounded-md bg-muted/50 text-center">
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <BarChart3 className="w-3 h-3 text-brand-blue" />
            </div>
            <p className="text-xs font-bold">{loc.avgRating ? `${((loc.avgRating / 5) * 100).toFixed(0)}%` : "--"}</p>
            <p className="text-[10px] text-muted-foreground">Coverage</p>
          </div>
          <div className="p-2 rounded-md bg-muted/50 text-center">
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <Star className="w-3 h-3 text-amber-400" />
            </div>
            <p className="text-xs font-bold">{loc.avgRating?.toFixed(1) || "--"}</p>
            <p className="text-[10px] text-muted-foreground">Rating</p>
          </div>
          <div className="p-2 rounded-md bg-muted/50 text-center">
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <Zap className="w-3 h-3 text-primary" />
            </div>
            <p className="text-xs font-bold">{loc.tokenBalance}</p>
            <p className="text-[10px] text-muted-foreground">Tokens</p>
          </div>
        </div>

        {confirmDelete ? (
          <div className="space-y-2">
            <p className="text-xs text-destructive font-medium">
              Delete "{loc.name}" and all its data? This cannot be undone.
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="destructive"
                className="flex-1"
                onClick={() => { setConfirmDelete(false); onDelete(); }}
                data-testid={`button-confirm-delete-${loc.id}`}
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Yes, Delete
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1"
                onClick={() => setConfirmDelete(false)}
                data-testid={`button-cancel-delete-${loc.id}`}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="flex-1"
              onClick={onViewDashboard}
              data-testid={`button-view-dashboard-${loc.id}`}
            >
              <Eye className="w-3 h-3 mr-1" />
              View Dashboard
              <ArrowRight className="w-3 h-3 ml-auto" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-muted-foreground hover:text-destructive"
              onClick={() => setConfirmDelete(true)}
              data-testid={`button-delete-${loc.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ClientsPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { selectedLocationId, setSelectedLocationId } = useLocationStore();
  const [, navigate] = useLocation();

  const { data, isLoading } = useQuery<{ role: string; locations: EnrichedLocation[] }>({
    queryKey: ["/api/clients/list", statusFilter],
    queryFn: () => fetch(`/api/clients/list?status=${statusFilter}`, { credentials: "include" }).then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); }),
  });

  const { toast } = useToast();
  const isAdmin = data?.role === "admin";
  const allLocations = data?.locations || [];

  const filteredLocations = allLocations.filter((loc) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return loc.name.toLowerCase().includes(q) || loc.city.toLowerCase().includes(q);
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/locations/${id}`);
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients/list"] });
      queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
      if (selectedLocationId === id) {
        const remaining = allLocations.filter(l => l.id !== id);
        setSelectedLocationId(remaining.length > 0 ? remaining[0].id : "");
      }
      toast({ title: "Location deleted", description: "The location and all its data have been removed." });
    },
    onError: (err: any) => {
      toast({ title: "Delete failed", description: err.message || "Could not delete location", variant: "destructive" });
    },
  });

  const handleViewDashboard = (locId: string) => {
    setSelectedLocationId(locId);
    navigate("/dashboard");
  };

  const handleExportCsv = () => {
    const headers = ["Name", "City", "Category", "Rating", "Reviews", "Tokens", "Status"];
    const rows = allLocations.map((l) => [
      l.name, l.city, l.category, l.avgRating || 0, l.totalReviews || 0, l.tokenBalance, l.status || "active",
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "locations-export.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[200px]" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24 space-y-6 overflow-y-auto h-full" data-testid="page-clients">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-clients-title">
            {isAdmin ? "All Clients" : "Your Locations"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {filteredLocations.length} location{filteredLocations.length !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={handleExportCsv} data-testid="button-export-csv">
              <Download className="w-3 h-3 mr-1" />
              Export CSV
            </Button>
          )}
          <Button size="sm" onClick={() => navigate("/setup")} data-testid="button-add-location">
            <Plus className="w-3 h-3 mr-1" />
            Add Location
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or city..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="input-search-locations"
          />
        </div>
        {isAdmin && (
          <Tabs value={statusFilter} onValueChange={setStatusFilter}>
            <TabsList>
              <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
              <TabsTrigger value="active" data-testid="tab-active">Active</TabsTrigger>
              <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
              <TabsTrigger value="inactive" data-testid="tab-inactive">Inactive</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </div>

      {filteredLocations.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <MapPin className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold" data-testid="text-no-locations">No Locations Found</h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-sm">
              {search ? "Try a different search term." : "Add your first location to get started."}
            </p>
            {!search && (
              <Button className="mt-4" onClick={() => navigate("/setup")} data-testid="button-add-first-location">
                <Plus className="w-4 h-4 mr-2" />
                Add Location
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredLocations.map((loc) => (
            <LocationCard
              key={loc.id}
              loc={loc}
              isSelected={loc.id === selectedLocationId}
              isAdmin={isAdmin}
              onViewDashboard={() => handleViewDashboard(loc.id)}
              onDelete={() => deleteMutation.mutate(loc.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
